import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react';
import { cn } from '@/lib/utils';

/**
 * Freezing a run of leading columns at the inline start of a `GentlyTable`
 * while the rest scroll horizontally. Opt in per column with
 * `TableColumn.pinned`.
 */

/** The bits of a column this module needs — avoids importing back from GentlyTable. */
interface PinnableColumn {
  id: PropertyKey;
  pinned?: boolean;
}

/** Where each pinned column sits, and which one closes the frozen block. */
export interface PinnedColumns {
  /** Column id → distance in px from the scroll container's inline start. */
  offsets: Map<string, number>;
  lastId: string | null;
}

/** Nothing frozen — a stable identity, so rows don't re-render on it. */
const EMPTY_PINNED_COLUMNS: PinnedColumns = {
  offsets: new Map(),
  lastId: null,
};

// Column ids are field names, so a newline can't collide with one.
const ID_SEPARATOR = '\n';

/**
 * The edge where frozen content meets scrolling content: a 1px line, plus a soft
 * shadow cast over whatever passes beneath it.
 *
 * The frozen columns and the frozen header are the same boundary on two axes, so
 * they get the same treatment and only the offsets differ — the header used to
 * carry a faint `border-b` in `--border` while the columns had a `--color-
 * muted-foreground` line, which read as two unrelated dividers.
 *
 * Both are box-shadow layers rather than borders, because Tailwind's preflight
 * sets `table { border-collapse: collapse }`: collapsed borders belong to the
 * table, not the cell, so a border on a `position: sticky` cell doesn't travel
 * with it and slides away as soon as the table scrolls. A box-shadow is painted
 * by the element itself and stays put. The `inset` layer's negative offset is
 * what puts the line on the trailing edge.
 *
 * Both have to stay written out as literals — Tailwind scans source for complete
 * class strings, so a composed one would never be generated.
 */
const FROZEN_EDGE_CLASS =
  'shadow-[inset_-1px_0_0_0_var(--color-muted-foreground),2px_0_4px_0_rgba(0,0,0,0.15)]';

/** The same edge under a frozen header row. See {@link FROZEN_EDGE_CLASS}. */
export const FROZEN_HEADER_EDGE_CLASS =
  'shadow-[inset_0_-1px_0_0_var(--color-muted-foreground),0_2px_4px_0_rgba(0,0,0,0.15)]';

/**
 * Place the frozen block from the pinned columns' measured widths.
 *
 * A pinned column's offset is the total width of the pinned columns before it.
 * Those columns are sized by the browser (by content under `sizeToContent`, by
 * the table's own layout otherwise), so `widths` comes from the DOM. It is
 * indexed by column position, which is why pinned columns have to lead the
 * array and be contiguous.
 */
function buildPinnedColumns(
  columns: PinnableColumn[],
  widths: number[],
): PinnedColumns {
  const offsets = new Map<string, number>();
  let offset = 0;
  let lastId: string | null = null;
  columns.forEach((column, index) => {
    if (!column.pinned) return;
    lastId = String(column.id);
    offsets.set(lastId, offset);
    offset += widths[index] ?? 0;
  });
  return { offsets, lastId };
}

/**
 * The rendered widths of the leading pinned header cells.
 *
 * Pinned columns are laid out like any other column, so their widths aren't
 * known from the config — they're read back off the header row and kept current
 * with a ResizeObserver, which covers content, container, and font changes
 * alike. Until the first measurement lands every offset is 0, so the frozen
 * columns stack at the inline start for one paint before settling.
 */
function usePinnedColumnWidths(
  pinnedKey: string,
): [RefObject<HTMLTableRowElement | null>, number[]] {
  const headerRowRef = useRef<HTMLTableRowElement>(null);
  const [widths, setWidths] = useState<number[]>([]);

  // Keyed on the pinned ids, not just their count: a same-length column swap
  // remounts the header cells, and the observer would be left on detached ones.
  useEffect(() => {
    const headerRow = headerRowRef.current;
    const pinnedCount =
      pinnedKey === '' ? 0 : pinnedKey.split(ID_SEPARATOR).length;
    if (!headerRow || pinnedCount === 0) {
      // Keep the empty array's identity — every unpinned table runs this, and a
      // fresh [] would cost them all a wasted render.
      setWidths((previous) => (previous.length === 0 ? previous : []));
      return;
    }
    const cells = [...headerRow.children].slice(0, pinnedCount);
    const measure = () => {
      const next = cells.map((cell) => cell.getBoundingClientRect().width);
      setWidths((previous) =>
        previous.length === next.length &&
        previous.every((width, index) => width === next[index])
          ? previous
          : next,
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    for (const cell of cells) observer.observe(cell);
    return () => observer.disconnect();
  }, [pinnedKey]);

  return [headerRowRef, widths];
}

/** The pinned column ids, joined — the identity the measurement re-runs on. */
function pinnedColumnKey(columns: PinnableColumn[]): string {
  const ids: string[] = [];
  for (const column of columns) {
    if (column.pinned) ids.push(String(column.id));
  }
  return ids.join(ID_SEPARATOR);
}

/**
 * The frozen block for `columns`, plus the ref to put on the header row it is
 * measured from.
 */
export function usePinnedColumns(columns: PinnableColumn[]): {
  headerRowRef: RefObject<HTMLTableRowElement | null>;
  pinnedColumns: PinnedColumns;
} {
  const pinnedKey = pinnedColumnKey(columns);
  const [headerRowRef, widths] = usePinnedColumnWidths(pinnedKey);
  return {
    headerRowRef,
    pinnedColumns:
      pinnedKey === ''
        ? EMPTY_PINNED_COLUMNS
        : buildPinnedColumns(columns, widths),
  };
}

/**
 * Cell attributes that freeze a pinned column in place; empty for an unpinned
 * one, which keeps unfrozen tables rendering exactly as before.
 *
 * A pinned cell needs an opaque background of its own, since unpinned cells
 * scroll underneath it, and the last one carries the divider marking the frozen
 * edge. It takes that background by inheritance rather than a fixed colour so
 * row state still tints the frozen columns — see `pinnedRowClass`.
 */
export function pinnedCellProps(
  column: PinnableColumn,
  pinned: PinnedColumns,
): { className?: string; style?: CSSProperties } {
  const id = String(column.id);
  const offset = pinned.offsets.get(id);
  if (offset === undefined) return {};
  return {
    className: cn('bg-inherit', pinned.lastId === id && FROZEN_EDGE_CLASS),
    style: { position: 'sticky', insetInlineStart: `${offset}px` },
  };
}

/**
 * The same for a header cell, plus the z-index that keeps a frozen header above a
 * column being dragged past it: dnd-kit lifts the dragged header to `z-20`, and
 * with an equal z the dragged one would win on DOM order (frozen columns lead the
 * row) and slide over the frozen block.
 *
 * Body cells deliberately don't take this. The sticky header carries `z-10`, and
 * the table is not a stacking context of its own — so a frozen *body* cell raised
 * above that would paint over the header row on vertical scroll. Inside the
 * header row, `z-30` only ever competes with the header's own cells.
 */
export function pinnedHeaderCellProps(
  column: PinnableColumn,
  pinned: PinnedColumns,
): { className?: string; style?: CSSProperties } {
  const props = pinnedCellProps(column, pinned);
  if (props.style === undefined) return props;
  return { ...props, className: cn(props.className, 'z-30') };
}

/**
 * Row background and state tints. With frozen columns both have to be opaque
 * for the pinned cells to inherit something that hides the scrolled cells
 * passing underneath; a translucent tint would let them show through.
 *
 * `bg-background`/`bg-accent` replace the shared TableRow's own
 * `hover:bg-muted/50` rather than layering over it — that tint is 50% alpha, so
 * inheriting it turns the frozen columns semi-transparent and the scrolled
 * cells read straight through them.
 */
export function pinnedRowClass(
  pinned: PinnedColumns,
  isExpanded: boolean,
): string {
  return pinned.lastId
    ? cn('bg-background hover:bg-accent', isExpanded && 'bg-accent')
    : cn('hover:bg-muted/50', isExpanded && 'bg-muted/30');
}

/**
 * Same for the header row, which is a TableRow too and so carries the same
 * translucent `hover:bg-muted/50`. It still highlights on hover — just with an
 * opaque colour, so the frozen header cells stay solid over the header text
 * scrolling behind them.
 */
export function pinnedHeaderRowClass(
  pinned: PinnedColumns,
): string | undefined {
  return pinned.lastId ? 'bg-background hover:bg-accent' : undefined;
}
